import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useAuth } from "@/providers/auth-provider";
import { isStepUpRequired } from "../api/stepup.api";
import { useDeleteAllSessions } from "../hooks/useAccessKeys";

/**
 * The Sessions control of the account page: once confirmed, every browser
 * session of the account is revoked on the server, this one included, and
 * only then does this browser sign out. A failed revocation keeps the session
 * and shows why, so the button never claims more than the server did.
 */
export function LogoutAllSessions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);

  const signOut = () => {
    logout();
    navigate({ to: "/login" });
  };

  const revokeAll = useDeleteAllSessions({ onSuccess: signOut });

  // A 401 means this session had already ended on its own, so nothing was revoked
  const sessionEnded = revokeAll.error?.status === 401 && !isStepUpRequired(revokeAll.error);

  const signInAgain = () => {
    logout();
    navigate({ to: "/login", search: { redirect: "/account" } });
  };

  const confirm = () => {
    revokeAll.mutate();
  };

  const openDialog = () => {
    // A reset while the request is in flight would detach its observer and lose the sign-out
    if (!revokeAll.isPending) revokeAll.reset();
    setOpen(true);
  };

  const error = revokeAll.error
    ? sessionEnded
      ? t("account.logoutAllSessionEnded")
      : (revokeAll.error.error ?? t("common.error"))
    : undefined;

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
      >
        <LogOut size={12} />
        {t("account.logoutAll")}
      </button>

      <ConfirmDialog
        open={open}
        onClose={sessionEnded ? signInAgain : () => setOpen(false)}
        onConfirm={sessionEnded ? signInAgain : confirm}
        title={t("account.logoutAll")}
        description={t("account.logoutAllConfirm")}
        error={error}
        confirmLabel={sessionEnded ? t("account.staleSession.action") : t("account.logoutAll")}
        cancelLabel={t("common.cancel")}
        destructive={!sessionEnded}
        isPending={revokeAll.isPending}
        closeOnConfirm={false}
      />
    </>
  );
}
